// CLI — `cloudgrid link` command.
//
// Per Decision 042 (Mode 1, §link), `link <entity>` binds the current folder
// to an existing grid entity — the recovery / re-binding common case, kept as
// the top-level alias of `wire folder <entity>`. Both run the one shared core
// (`wireFolderAction` in `wire-folder.ts`), so they are functionally identical.
//
// The `repair` subcommand is retained: it heals a stale `org_slug` in
// `.cloudgrid/link.json` (backlog 309). Migration 003 (2026-05-03) renamed the
// canonical org slug `_system_` → `system` on the server but did not touch
// per-checkout `link.json` files. The `org_slug` field is display-only —
// resolution is by `org_id` (backlog 308) — but a stale value still shows up in
// `unplug`/`info` output and in mismatch errors, so `repair` re-resolves it
// server-side and rewrites it.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { CloudGridError } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import { readLinkOrRecover, writeLink } from '../services/link.js';
import { wireFolderAction } from './wire-folder.js';
import { addGridOption } from '../grid-opt.js';

export interface LinkRepairOpts {
  dryRun?: boolean;
}

/**
 * Re-resolve the link's `org_id` to its current canonical slug and rewrite
 * `link.json` if the stored `org_slug` differs.
 *
 * Idempotent: a no-op (exit 0) when already canonical. With `--dry-run` it
 * prints the planned change and writes nothing. Throws `CloudGridError`
 * when there is no link or the `org_id` can't be resolved to an accessible
 * org.
 */
export async function runLinkRepair(
  client: IV2Client,
  opts: LinkRepairOpts,
  env: { cwd: string },
): Promise<void> {
  // backlog 309 motivating case: the Console wrote link.json on CLI 0.8.3,
  // before `kind` existed (backlog 090). Strict readLink throws
  // MalformedLinkError on exactly the file this verb exists to heal, so use
  // the recovering reader — it re-resolves kind server-side and writes it
  // back, then we proceed to fix the stale org_slug below.
  // Opt out of on-read legacy-slug auto-repair (backlog 309): this verb
  // reports the change (and supports --dry-run), so it must see the *stale*
  // slug rather than have the read silently fix it first.
  const link = await readLinkOrRecover(client, env.cwd, { autoRepairOrgSlug: false });
  if (link === null) {
    throw new CloudGridError(
      "This directory isn't linked to a CloudGrid node. Run: cloudgrid init <kind> <name>",
    );
  }

  const orgs = await client.getOrgs();
  const match = orgs.find((o) => o.org_id === link.org_id);
  if (!match) {
    throw new CloudGridError(
      `Could not resolve org_id ${link.org_id} to a grid you can access. ` +
        `The link may point at a grid you were removed from.`,
    );
  }

  if (match.slug === link.org_slug) {
    process.stdout.write(
      `link.json is already canonical: org_slug '${link.org_slug}' (org_id ${link.org_id}).\n`,
    );
    return;
  }

  if (opts.dryRun) {
    process.stdout.write(
      `Would repair link.json: org_slug '${link.org_slug}' → '${match.slug}' ` +
        `(org_id ${link.org_id} unchanged). Re-run without --dry-run to apply.\n`,
    );
    return;
  }

  writeLink({ ...link, org_slug: match.slug }, env.cwd);
  process.stdout.write(
    `Repaired link.json: org_slug '${link.org_slug}' → '${match.slug}' ` +
      `(org_id ${link.org_id} unchanged).\n`,
  );
}

const repairSubcommand = new Command('repair')
  .description("Re-resolve a stale org_slug in .cloudgrid/link.json")
  .option('--dry-run', "Show the planned change without writing")
  .action(async (opts: LinkRepairOpts) => {
    try {
      const client = await getV2Client();
      await runLinkRepair(client, opts, { cwd: process.cwd() });
    } catch (err) {
      await handleV2Error(err);
    }
  });

export const linkCommand = new Command('link')
  .description('Bind this folder to an existing grid entity (alias of `wire folder`)')
  .argument('[entity]', 'Entity slug or id to bind this folder to');
addGridOption(linkCommand, 'Grid to resolve the entity in (defaults to the active grid)');
linkCommand
  .addCommand(repairSubcommand)
  .action(wireFolderAction);
